<template>
  <div>
    <Transition name="fade" mode="out-in">
      <div v-if="hasSelectedMarkers" class="selected-markers-table-wrapper">
        <v-card outlined>
          <SelectedMarkersGroupInfo :group="group" />

          <v-data-table
            v-model="selectedAppointments"
            @dblclick:row="showMarkerInfo"
            :headers="headers"
            :items="markers"
            :items-per-page="999"
            :no-data-text="$t('appointment.table.noData')"
            :loading-text="$t('appointment.table.loading')"
            class="selected-markers-table"
            mobile-breakpoint="20"
            item-key="id"
            key="id"
            hide-default-footer
            disable-sort
            show-select
          >
            <template v-slot:item.title="{ item }">
              <span class="caption">
                {{ $t(`appointment.type.${item.type}`) }}
              </span>
              <v-tooltip v-if="item.code" top>
                <template v-slot:activator="{ on, attrs }">
                  <v-chip v-bind="attrs" v-on="on" x-small>
                    {{ item.code }}
                  </v-chip>
                </template>
                <span>
                  {{ $t('mapDrawing.appointmentCode') }}
                </span>
              </v-tooltip>
            </template>
            <template v-slot:item.date="{ item }">
              <span class="caption">
                {{ catalogDate(item.date) }}
              </span>
            </template>
            <template v-slot:item.status="{ item }">
              <span class="caption">
                {{ $t(`appointment.status.${item.status}`) }}
              </span>
            </template>
            <template v-slot:item.confirmation="{ item }">
              <ShowConfirmedStatus
                :confirmation="item.confirmation"
              />
            </template>
            <template v-if="!group" v-slot:item.group_code="{ item }">
              <span v-if="item.group_code">
                <v-chip x-small>
                  {{ item.group_code }}
                </v-chip>
              </span>
              <span v-else>---</span>
            </template>
            <template v-slot:item.actions="{ item }">
              <SelectedMarkersMenu
                class="selected-markers-menu"
                @modalAndAppointment="modalAndAppointment"
                @refreshMap="refreshMap"
                :appointment="item"
                :group="group"
              />
            </template>
          </v-data-table>

          <v-spacer />

          <SelectedMarkersButtons
            @createGroup="createGroup"
            :selectedAppointments="selectedAppointments"
            :canCreateAGroup="canCreateAGroup"
            :hasGroupId="hasGroupId"
            :groupHost="groupHost"
            :isLoading="isLoading"
            class="selected-markers-buttons"
            ref="selectedMarkersButtons"
          />
        </v-card>
      </div>
    </Transition>

    <SelectedMarkersModals
      @refreshMap="refreshMap"
      :markers="markers"
      :appointment="currentAppointment"
      :hasSelectedMarkers="hasSelectedMarkers"
      :hasGroupId="hasGroupId"
      :groupId="groupId"
      :groupHost="groupHost"
      :selectedAppointments="selectedAppointments"
      :category="category"
    />
  </div>
</template>

<script>
import { catalogDate } from '@/util/catalog'
import { mapGetters, mapActions } from 'vuex'
import { selectedMarkersHeaders } from '../listHeaders/appointmentHeaders'

import SelectedMarkersMenu from './fragments/SelectedMarkersMenu'
import SelectedMarkersModals from './fragments/SelectedMarkersModals'
import SelectedMarkersGroupInfo from './fragments/SelectedMarkersGroupInfo'
import SelectedMarkersButtons from './fragments/SelectedMarkersButtons'
import InformationTooltip from '../../components/app/InformationTooltip'

import ShowConfirmedStatus from './fragments/ShowConfirmedStatus'

export default {
  name: 'SelectedMarkersTable',

  components: {
    SelectedMarkersMenu,
    SelectedMarkersModals,
    SelectedMarkersGroupInfo,
    SelectedMarkersButtons,
    InformationTooltip,

    ShowConfirmedStatus,
  },

  props: {
    group: {
      type: Object,
      required: false,
    },

    markers: {
      type: Array,
      required: false,
    },

    category: {
      type: String,
      required: false,
    },
  },

  computed: {
    ...mapGetters(['isLoading']),

    groupId() {
      return this.group?.id
    },

    hasGroupId() {
      return this.group?.id !== undefined
    },

    hasSelectedMarkers() {
      return this.markers.length > 0
    },

    headers() {
      if (this.group) {
        return selectedMarkersHeaders.filter(header => header.value !== 'group_code')
      }
      return selectedMarkersHeaders
    },

    groupHost() {
      return this.group?.host
    },

    showCloseTooltip() {
      this.$refs.selectedMarkersButtons?.showCloseTooltip()
    },

    canCreateAGroup() {
      if (this.selectedAppointments?.length >= 1) {
        for (let i = 0; i < this.selectedAppointments?.length; i++) {
          if (!this.GROUPABLE_STATUSES.includes(this.selectedAppointments[i].status)) {
            return false
          }
        }

        return true
      }
    },

    GROUPABLE_STATUSES() {
      return [
        'OPEN',
        'REJECTED',
        'DRAFT',
      ]
    },
  },

  watch: {
    markers() {
      this.selectedAppointments = []
    }
  },

  methods: {
    ...mapActions(['openModal', 'partnerCreateAppointmentGroup', 'partnerSendConfirmationGroup', 'fetchConfirmAppointmentData']),

    createGroup() {
      const appointmentIds = []

      if (this.selectedAppointments.length <= 0) {
        this.markers.map((mark) => appointmentIds.push(mark.id))
      } else {
        this.selectedAppointments.map((mark) => appointmentIds.push(mark.id))
      }

      const group = {
        appointments: appointmentIds,
        latitude: this.markers[0].latitude,
        longitude: this.markers[0].longitude,
      }

      this.partnerCreateAppointmentGroup(group)
        .then(() => this.refreshMap())
    },

    refreshMap() {
      this.$emit('refreshMap')
    },

    closeSelection() {
      this.$emit('closeSelection', this.hasGroupId)
    },

    modalAndAppointment({ modal, appointment, confirmModal }) {
      if (confirmModal) this.fetchConfirmAppointmentData(appointment?.id)

      this.currentAppointment = appointment
      this.openModal(modal)
    },

    showMarkerInfo(_, { item }) {
      if (!item?.id) {
        this.$store.messages.showErrorMessage('mapDrawing.messages.appointmentNotFound')
        return
      }

      this.$emit('showMarkerInfo', item?.id)
    },
  },

  data() {
    return {
      catalogDate,
      currentAppointment: null,
      selectedAppointments: [],
    }
  }
}
</script>

<style scoped>
.selected-markers-table-wrapper {
  position: absolute;
  top: 4px;
  right: 5px;
  width: 100%;
  max-width: 900px;
  overflow: hidden;
  border-radius: 8px;
  background-color: transparent;
  box-shadow: -2px 6px 12px 0 rgba(0, 0, 0, 0.2) !important;

  scrollbar-color: #21566e rgba(182, 203, 253, 0.5);

  &::-webkit-scrollbar {
    width: 12px;
    border-radius: 0 8px 8px 0;
  }

  &::-webkit-scrollbar-thumb {
    background-color: #888;
  }

  &::-webkit-scrollbar-track {
    background-color: #f4f4f4;
  }

  & table {
    width: 100%;
    border-collapse: collapse;
    background-color: transparent !important;
  }

  & th {
    & span {
      font-size: 12px !important;
      font-weight: bold !important;
    }
  }

  & td {
    font-size: 13px !important;
  }

  & tbody {
    & tr {
      cursor: pointer;
    }
  }

  & > div {
    width: 100%;
    background-color: rgba(255, 255, 255, 0.65);
    backdrop-filter: blur(10px);
  }
}

.selected-markers-table {
  width: 100%;
  max-height: 450px;
  overflow-y: auto;
  backdrop-filter: blur(10px);
}

.selected-markers-menu {
  opacity: 0;
}

.selected-markers-table table tbody tr:hover .selected-markers-menu {
  opacity: 1;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.5s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
